interface CaseStudy {
  num: string;
  tag: string;
  title: string;
  client: string;
  summary: string;
  scope: string[];
  stack: string[];
  accent: string;
  /** Still stand-in content — renders the "swap in real work" label. */
  placeholder?: boolean;
}

// Real work. Deliberately qualitative — no invented metrics.
const CASES: CaseStudy[] = [
  {
    num: "01",
    tag: "Applied AI",
    title: "Slimsoft Advanced Memory",
    client: "Slimsoft product · in production",
    summary: "A self-hosted structured memory system and MCP server that gives modern AI tools — Claude, ChatGPT, and anything MCP-speaking — persistent memory across sessions. Structured facts and tabular records alike: recipes, finances, health, nutrition, life-admin. Shareable with family and friends, owned by you.",
    scope: [
      "Structured + tabular memory store, self-hosted",
      "MCP server compatible with all modern AI tools",
      "Sharing model for family & friends",
    ],
    stack: ["MCP", "Claude", "ChatGPT", "SQLite / Postgres"],
    accent: "var(--beam)",
  },
  {
    num: "02",
    tag: "Network & IT",
    title: "Hotel network, ground-up",
    client: "Boutique hotel · Downtown Vancouver · ongoing",
    summary: "A full UniFi build for a pre-opening hotel — surveillance, staff and guest WiFi, and segmented networks, all subscription-free. Recording stays on-premises with RAID-protected storage, and the camera system keeps recording through network and power outages. The whole property is remotely managed and controlled, with runbooks and as-builts for handover.",
    scope: [
      "On-prem recording · RAID 5–10 data protection",
      "Surveillance immune to network & power outages",
      "Full remote management — no subscriptions",
    ],
    stack: ["Ubiquiti UniFi", "UniFi Protect", "Zone firewall", "Teleport VPN"],
    accent: "var(--solar)",
  },
  {
    num: "03",
    tag: "Smart home",
    title: "Consolidated smart-home build",
    client: "Private residence",
    summary: "Unified a house full of disconnected ecosystems — cameras, locks, lights, climate, voice — under one self-hosted Home Assistant brain. Local-first automations, no cloud dependency for the essentials, and one app instead of nine.",
    scope: [
      "Home Assistant on Proxmox, local-first",
      "Cameras, locks, climate, and voice unified",
      "Agentic automations and remote access",
    ],
    stack: ["Home Assistant", "Proxmox", "Zigbee / Thread", "UniFi Protect"],
    accent: "var(--solar)",
  },
  {
    num: "04",
    tag: "Agent infrastructure",
    title: "Custom MCP servers & plugin build pipelines",
    client: "Slimsoft internal · in production",
    summary: "A private plugin marketplace and the build pipeline behind it — custom MCP servers that give agents real reach into mail, calendar, network gear, and home infrastructure, packaged as versioned plugins that install the same way on every machine and surface. One source of truth, reproducible installs, no hand-copied config.",
    scope: [
      "Custom MCP servers for real systems",
      "Plugin build & release pipeline",
      "Versioned installs across machines and surfaces",
    ],
    stack: ["MCP", "Claude Code", "TypeScript", "Bash · CI"],
    accent: "var(--bio)",
  },
];

export function Work() {
  return (
    <section
      id="work"
      style={{
        position: "relative",
        padding: "var(--section-y, 120px) 32px",
        background: "color-mix(in oklab, var(--paper-soft) 82%, transparent)",
        backdropFilter: "blur(6px)",
        borderTop: "1px solid var(--hairline)",
        borderBottom: "1px solid var(--hairline)",
      }}
    >
      <div className="container">
        <div
          style={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
            gap: 32,
            marginBottom: 56,
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 20, maxWidth: 640 }}>
            <span className="rule-eyebrow">Selected work</span>
            <h2
              style={{
                margin: 0,
                fontFamily: "var(--font-display)",
                fontWeight: 300,
                fontSize: "clamp(36px, 5vw, 60px)",
                lineHeight: 1.02,
                letterSpacing: "-0.02em",
                color: "var(--ink)",
              }}
            >
              A small portfolio,
              <br />
              built carefully.
            </h2>
          </div>
          <span className="tape" style={{ alignSelf: "flex-end" }}>
            Client names on request
          </span>
        </div>

        <div style={{ display: "flex", flexDirection: "column" }}>
          {CASES.map((study, i) => (
            <article
              key={study.num}
              className="case-row"
              style={{
                padding: "40px 0",
                borderTop: "1px solid var(--hairline)",
                borderBottom: i === CASES.length - 1 ? "1px solid var(--hairline)" : "none",
                display: "grid",
                gridTemplateColumns: "minmax(0, 1.1fr) minmax(0, 1fr)",
                gap: 56,
                alignItems: "flex-start",
                transition: "background var(--dur-base) var(--ease-out)",
              }}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                  <span className="num">— {study.num}</span>
                  <span
                    style={{
                      fontFamily: "var(--font-display)",
                      fontWeight: 500,
                      fontSize: 11,
                      letterSpacing: "0.18em",
                      textTransform: "uppercase",
                      padding: "4px 10px",
                      borderRadius: 999,
                      background: `color-mix(in oklab, ${study.accent} 14%, transparent)`,
                      color: study.accent,
                      border: `1px solid color-mix(in oklab, ${study.accent} 28%, transparent)`,
                    }}
                  >
                    {study.tag}
                  </span>
                  {study.placeholder && (
                    <span
                      style={{
                        fontFamily: "var(--font-mono)",
                        fontSize: 10,
                        letterSpacing: "0.16em",
                        textTransform: "uppercase",
                        color: "var(--ink-5)",
                      }}
                    >
                      · Placeholder, swap in real work
                    </span>
                  )}
                </div>
                <h3
                  style={{
                    margin: 0,
                    fontFamily: "var(--font-display)",
                    fontWeight: 400,
                    fontSize: 36,
                    lineHeight: 1.1,
                    letterSpacing: "-0.015em",
                    color: "var(--ink)",
                  }}
                >
                  {study.title}
                </h3>
                <span style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--ink-4)", letterSpacing: "0.02em" }}>
                  {study.client}
                </span>
                <p style={{ margin: 0, maxWidth: 540, fontFamily: "var(--font-body)", fontSize: 15, lineHeight: 1.65, color: "var(--ink-2)" }}>
                  {study.summary}
                </p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
                  {study.stack.map((tech) => (
                    <span
                      key={tech}
                      style={{
                        fontFamily: "var(--font-mono)",
                        fontSize: 11,
                        padding: "4px 9px",
                        borderRadius: "var(--r-sm)",
                        background: "var(--surface)",
                        color: "var(--ink-3)",
                        border: "1px solid var(--hairline)",
                      }}
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>

              <div
                style={{
                  background: "var(--surface)",
                  border: "1px solid var(--hairline)",
                  borderRadius: "var(--r-lg)",
                  padding: "26px 28px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 14,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    paddingBottom: 14,
                    borderBottom: "1px solid var(--hairline)",
                  }}
                >
                  <span
                    style={{
                      fontFamily: "var(--font-display)",
                      fontWeight: 500,
                      fontSize: 11,
                      letterSpacing: "0.18em",
                      textTransform: "uppercase",
                      color: "var(--ink-3)",
                    }}
                  >
                    Scope of work
                  </span>
                  <span style={{ width: 8, height: 8, borderRadius: 999, background: study.accent, opacity: 0.85 }} />
                </div>
                <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 12 }}>
                  {study.scope.map((item, j) => (
                    <li
                      key={item}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "auto 1fr",
                        gap: 14,
                        alignItems: "flex-start",
                        fontFamily: "var(--font-body)",
                        fontSize: 14,
                        lineHeight: 1.55,
                        color: "var(--ink-2)",
                      }}
                    >
                      <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--ink-4)", letterSpacing: "0.04em", paddingTop: 3 }}>
                        {String(j + 1).padStart(2, "0")}
                      </span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          ))}
        </div>

        <div style={{ marginTop: 48, textAlign: "center", fontFamily: "var(--font-body)", fontSize: 14, color: "var(--ink-3)" }}>
          Want the long version of any of these?{" "}
          <a href="#contact" style={{ color: "var(--beam)", borderBottom: "1px solid var(--beam-halo)" }}>
            Email me
          </a>
          .
        </div>
      </div>
    </section>
  );
}
